import { formatCatalogTaxonomyLabel } from "@/lib/music-taxonomy";
import type { MediaFormatDetail, MediaFormatSummaryItem } from "@garimpa/lib/formats";

type TranslationLookup = ((key: never) => string) & {
  has: (key: never) => boolean;
};

interface ReleaseFormatDisplayInput {
  formatMessages: TranslationLookup;
  featureMessages?: TranslationLookup;
}

export interface ReleaseFormatSummaryItem {
  key: string;
  format: string;
  quantity: number;
  label: string;
  text: string;
}

export interface ReleaseFormatDetailRow {
  key: string;
  seq: number;
  format: string;
  quantity: number;
  label: string;
  text: string | null;
  features: string[];
  featureLabels: string[];
  detailText: string;
  compactText: string;
  extraText: string | null;
}

export interface ReleaseFormatDetailSection {
  key: string;
  format: string;
  quantity: number;
  label: string;
  featureLabels: string[];
  title: string;
  extraTitle: string | null;
  rows: ReleaseFormatDetailRow[];
}

export interface ReleaseFormatDisplay {
  summaryItems: ReleaseFormatSummaryItem[];
  summaryText: string | null;
  detailRows: ReleaseFormatDetailRow[];
  detailSections: ReleaseFormatDetailSection[];
}

export interface ReleaseFormatSummaryDisplay {
  summaryItems: ReleaseFormatSummaryItem[];
  summaryText: string | null;
}

interface MutableSummaryItem extends ReleaseFormatSummaryItem {
  firstSeq: number;
}

interface MutableDetailRow extends ReleaseFormatDetailRow {
  firstSeq: number;
}

interface MutableDetailSection extends Omit<ReleaseFormatDetailSection, "rows"> {
  firstSeq: number;
  rows: MutableDetailRow[];
  rowsByKey: Map<string, MutableDetailRow>;
}

export function buildReleaseFormatDisplay(
  details: MediaFormatDetail[],
  input: ReleaseFormatDisplayInput,
): ReleaseFormatDisplay {
  const sortedDetails = [...details].sort((a, b) => a.seq - b.seq);
  const summaryByFormat = new Map<string, MutableSummaryItem>();
  const detailRows: ReleaseFormatDetailRow[] = [];
  const sectionsByFormat = new Map<string, MutableDetailSection>();
  const formatCount = new Set(sortedDetails.map((detail) => detail.format)).size;

  for (const detail of sortedDetails) {
    const label = formatLabel(input.formatMessages, detail.format);
    const featureLabels = detail.features.map((feature) =>
      input.featureMessages ? formatLabel(input.featureMessages, feature) : fallbackLabel(feature),
    );
    const quantity = normalizeQuantity(detail.quantity);
    const includeFormatPrefix =
      sortedDetails.length <= 2 || featureLabels.length > 0 || formatCount > 1;

    const summary = summaryByFormat.get(detail.format);
    if (summary) {
      summary.quantity += quantity;
      summary.text = formatQuantityLabel(summary.quantity, summary.label);
    } else {
      summaryByFormat.set(detail.format, {
        key: detail.format,
        format: detail.format,
        quantity,
        label,
        text: formatQuantityLabel(quantity, label),
        firstSeq: detail.seq,
      });
    }

    const fullDetailText = formatDetailLabel({
      label,
      quantity,
      text: detail.text,
      featureLabels,
    });
    const row = {
      key: `${detail.seq}-${detail.format}-${detail.text ?? ""}-${detail.features.join("|")}`,
      seq: detail.seq,
      format: detail.format,
      quantity,
      label,
      text: detail.text,
      features: detail.features,
      featureLabels,
      detailText: fullDetailText,
      compactText: formatCompactDetailLabel({
        label,
        quantity,
        text: detail.text,
        featureLabels,
      }),
      extraText: formatExtraDetailText({
        label,
        quantity,
        text: detail.text,
        featureLabels,
        includeFormatPrefix,
      }),
    };
    detailRows.push(row);

    const sectionKey = `${detail.format}:${detail.features.join("|")}`;
    let section = sectionsByFormat.get(sectionKey);
    if (!section) {
      section = {
        key: sectionKey,
        format: detail.format,
        quantity: 0,
        label,
        featureLabels,
        title: "",
        extraTitle: null,
        rows: [],
        firstSeq: detail.seq,
        rowsByKey: new Map(),
      };
      sectionsByFormat.set(sectionKey, section);
    }
    section.quantity += quantity;
    section.title = formatDetailSectionTitle(section.quantity, label, featureLabels);

    const rowKey = `${detail.format}:${detail.features.join("|")}:${detail.text ?? ""}`;
    const groupedRow = section.rowsByKey.get(rowKey);
    if (groupedRow) {
      groupedRow.quantity += quantity;
      groupedRow.detailText = formatDetailLabel({
        label,
        quantity: groupedRow.quantity,
        text: groupedRow.text,
        featureLabels,
      });
      groupedRow.compactText = formatCompactDetailLabel({
        label,
        quantity: groupedRow.quantity,
        text: groupedRow.text,
        featureLabels,
      });
      groupedRow.extraText = formatExtraDetailText({
        label,
        quantity: groupedRow.quantity,
        text: groupedRow.text,
        featureLabels,
        includeFormatPrefix,
      });
    } else {
      const mutableRow: MutableDetailRow = { ...row, firstSeq: detail.seq };
      section.rowsByKey.set(rowKey, mutableRow);
      section.rows.push(mutableRow);
    }
  }

  const summaryItems = [...summaryByFormat.values()]
    .sort((a, b) => a.firstSeq - b.firstSeq)
    .map(({ firstSeq: _firstSeq, ...summary }) => summary);

  const detailSections = [...sectionsByFormat.values()]
    .sort((a, b) => a.firstSeq - b.firstSeq)
    .map(({ firstSeq: _firstSeq, rowsByKey: _rowsByKey, rows, ...section }, _index, sections) => ({
      ...section,
      extraTitle: formatDetailSectionExtraTitle(section, sections.length),
      rows: rows
        .sort((a, b) => a.firstSeq - b.firstSeq)
        .map(({ firstSeq: _rowFirstSeq, ...row }) => row),
    }));

  return {
    summaryItems,
    summaryText: summaryItems.length > 0 ? summaryItems.map((item) => item.text).join(" + ") : null,
    detailRows,
    detailSections,
  };
}

export function buildReleaseFormatSummaryDisplay(
  summary: MediaFormatSummaryItem[],
  input: ReleaseFormatDisplayInput,
): ReleaseFormatSummaryDisplay {
  const summaryByFormat = new Map<string, MutableSummaryItem>();

  summary.forEach((item, index) => {
    if (!item.format) return;
    const label = formatLabel(input.formatMessages, item.format);
    const quantity = normalizeQuantity(item.quantity);
    const existing = summaryByFormat.get(item.format);

    if (existing) {
      existing.quantity += quantity;
      existing.text = formatQuantityLabel(existing.quantity, existing.label);
      return;
    }

    summaryByFormat.set(item.format, {
      key: item.format,
      format: item.format,
      quantity,
      label,
      text: formatQuantityLabel(quantity, label),
      firstSeq: index,
    });
  });

  const summaryItems = [...summaryByFormat.values()]
    .sort((a, b) => a.firstSeq - b.firstSeq)
    .map(({ firstSeq: _firstSeq, ...summaryItem }) => summaryItem);

  return {
    summaryItems,
    summaryText: summaryItems.length > 0 ? summaryItems.map((item) => item.text).join(" + ") : null,
  };
}

function formatLabel(messages: TranslationLookup, key: string): string {
  const messageKey = key as never;
  return messages.has(messageKey) ? messages(messageKey) : fallbackLabel(key);
}

function fallbackLabel(key: string): string {
  return formatCatalogTaxonomyLabel(key);
}

function formatQuantityLabel(quantity: number, label: string): string {
  return `${quantity}x ${label}`;
}

function normalizeQuantity(quantity: number): number {
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
}

function formatDetailLabel({
  label,
  quantity,
  text,
  featureLabels,
}: {
  label: string;
  quantity: number;
  text: string | null;
  featureLabels: string[];
}): string {
  const details = [...featureLabels];
  if (text) {
    details.push(text);
  }
  const prefix = formatQuantityLabel(quantity, label);
  return details.length > 0 ? `${prefix} · ${details.join(", ")}` : prefix;
}

function formatCompactDetailLabel({
  label,
  quantity,
  text,
  featureLabels,
}: {
  label: string;
  quantity: number;
  text: string | null;
  featureLabels: string[];
}): string {
  if (text) return quantity > 1 ? `${quantity}x ${text}` : text;
  if (featureLabels.length > 0) {
    const features = featureLabels.join(", ");
    return quantity > 1 ? `${quantity}x ${features}` : features;
  }
  return formatQuantityLabel(quantity, label);
}

function formatExtraDetailText({
  label,
  quantity,
  text,
  featureLabels,
  includeFormatPrefix,
}: {
  label: string;
  quantity: number;
  text: string | null;
  featureLabels: string[];
  includeFormatPrefix: boolean;
}): string {
  const formatPrefix = formatQuantityLabel(quantity, label);
  const prefix =
    featureLabels.length > 0 ? `${formatPrefix} ${featureLabels.join(", ")}` : formatPrefix;

  if (!text) return prefix;
  const textLabel = quantity > 1 ? `${quantity}x ${text}` : text;

  return includeFormatPrefix ? `${prefix} · ${text}` : textLabel;
}

function formatDetailSectionTitle(
  quantity: number,
  label: string,
  featureLabels: string[],
): string {
  const title = formatQuantityLabel(quantity, label);
  return featureLabels.length > 0 ? `${title} · ${featureLabels.join(", ")}` : title;
}

function formatDetailSectionExtraTitle(
  section: Omit<ReleaseFormatDetailSection, "rows">,
  sectionCount: number,
): string | null {
  if (section.featureLabels.length > 0) return section.featureLabels.join(", ");
  if (sectionCount <= 1) return null;
  return section.quantity > 1
    ? formatQuantityLabel(section.quantity, section.label)
    : section.label;
}
